import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { posApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-POS-03.10 / 03.11 / 03.12 — Gift card management.
 *
 * Issuance form on the left, recent cards list on the right,
 * balance-check dialog for "does this card still have money on it".
 * Actual redemption happens via the POS cart (gift_card payment method).
 */
export function GiftCardsPage() {
  const qc = useQueryClient();
  const [showIssue, setShowIssue] = useState(false);
  const [checkCode, setCheckCode] = useState('');

  const listQuery = useQuery({
    queryKey: ['gift-cards'],
    queryFn: () => posApi.listGiftCards({ limit: 50 }),
  });

  return (
    <div className="p-6">
      <PageHeader
        title="Gift Cards"
        subtitle="FR-POS-03.10 / 03.11 / 03.12 — Issue, redeem, check balance"
        actions={<button className="btn-primary" onClick={() => setShowIssue(true)}>+ Issue card</button>}
      />

      <div className="card p-4 mb-6">
        <h2 className="font-semibold mb-2 text-sm">Balance check</h2>
        <div className="flex gap-2">
          <input
            className="input max-w-xs font-mono text-sm"
            placeholder="GC-XXXX-XXXX-XXXX"
            value={checkCode}
            onChange={(e) => setCheckCode(e.target.value)}
          />
          <BalanceCheckButton code={checkCode} />
        </div>
      </div>

      {listQuery.isLoading ? <LoadingState /> : listQuery.error ? <ErrorState error={listQuery.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Issued</th>
                <th>Issued by</th>
                <th>To customer</th>
                <th className="text-right">Initial</th>
                <th className="text-right">Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!listQuery.data || listQuery.data.length === 0 ? (
                <tr><td colSpan={7}><EmptyState message="No gift cards issued yet." /></td></tr>
              ) : listQuery.data.map((c: any) => (
                <tr key={c.id}>
                  <td className="font-mono text-xs">{c.code}</td>
                  <td className="text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="text-xs">{c.issued_by_name ?? '—'}</td>
                  <td className="text-xs">{c.issued_to_customer_name ?? '—'}</td>
                  <td className="text-right">{fmtCurrency(parseFloat(c.initial_value))}</td>
                  <td className="text-right font-semibold">{fmtCurrency(parseFloat(c.current_balance))}</td>
                  <td>
                    <span className={`badge ${
                      c.status === 'active' ? 'bg-sage-50 text-sage-700 ring-sage-200' :
                      c.status === 'exhausted' ? 'bg-canvas-subtle text-ink-600 ring-ink-200' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showIssue && (
        <IssueGiftCardModal
          onClose={() => setShowIssue(false)}
          onIssued={() => {
            qc.invalidateQueries({ queryKey: ['gift-cards'] });
            setShowIssue(false);
          }}
        />
      )}
    </div>
  );
}

function BalanceCheckButton({ code }: { code: string }) {
  const mut = useMutation({
    mutationFn: () => posApi.checkGiftCard(code),
    onSuccess: (data) => {
      toast.success(
        `${data.code}: ${fmtCurrency(data.currentBalance)} of ${fmtCurrency(data.initialValue)} (${data.status})`,
        { duration: 6000 }
      );
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <button
      className="btn-secondary"
      disabled={!code.trim() || mut.isPending}
      onClick={() => mut.mutate()}
    >
      {mut.isPending ? 'Checking…' : 'Check'}
    </button>
  );
}

function IssueGiftCardModal({ onClose, onIssued }: { onClose: () => void; onIssued: () => void }) {
  const [initialValue, setInitialValue] = useState('25');
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => posApi.issueGiftCard({
      initialValue: parseFloat(initialValue) || 0,
      code: code.trim() || undefined,
      pin: pin.trim() || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (data) => {
      toast.success(`Issued ${data.code} for ${fmtCurrency(data.initialValue)}`, { duration: 6000 });
      onIssued();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = parseFloat(initialValue) > 0;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">POS · Gift cards</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Issue gift card</h2>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Initial value (USD) <span className="text-danger">*</span></label>
          <input
            type="number" step="0.01" min="0.01" autoFocus
            className="input tabular-nums"
            value={initialValue}
            onChange={(e) => setInitialValue(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Code <span className="font-normal text-ink-400 normal-case tracking-normal">(leave blank to auto-generate)</span></label>
          <input
            className="input font-mono"
            placeholder="GC-XXXX-XXXX-XXXX"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label className="label">PIN <span className="font-normal text-ink-400 normal-case tracking-normal">(optional, 4–20 characters)</span></label>
          <input className="input" value={pin} onChange={(e) => setPin(e.target.value)} />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Issuing…' : 'Issue gift card'}
        </button>
      </div>
    </Modal>
  );
}
